'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import {
  ArrowLeft,
  ArrowSquareOut,
  FilePdf,
  ImageSquare,
  File as FileIcon,
  Trash,
  UploadSimple,
  CheckCircle,
  Sparkle,
  WarningCircle,
  ChatCircleDots,
} from '@phosphor-icons/react';
import { GalexiaLogo } from '@/components/galexia-logo';
import { ShareButton } from '@/components/share-button';

interface Doc {
  id: string;
  name: string;
  mimeType: string;
  sizeBytes: number;
  fileUrl: string;
  hasParsedMarkdown: boolean;
  parsedAt?: string;
  extractionProvider?: string;
  hasExtractedSummary: boolean;
  createdAt: string;
}

const ALLOWED_HINT = 'PDF, Office docs, spreadsheets, text, CSV, images · up to 25MB';

export function DocumentsClient() {
  const router = useRouter();
  const [docs, setDocs] = useState<Doc[]>([]);
  const [loading, setLoading] = useState(true);
  const [notMigrated, setNotMigrated] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchDocs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/documents');
      const data = await res.json();
      setDocs(data.documents ?? []);
      setNotMigrated(!!data.notMigrated);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchDocs();
  }, [fetchDocs]);

  const handleFiles = useCallback(async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setUploadError(null);
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        const form = new FormData();
        form.append('file', file);
        const res = await fetch('/api/documents', { method: 'POST', body: form });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          setUploadError(data.error || 'Upload failed');
          break;
        }
      }
      await fetchDocs();
    } finally {
      setUploading(false);
    }
  }, [fetchDocs]);

  const handleArchive = async (id: string) => {
    try {
      await fetch(`/api/documents/${id}/archive`, { method: 'POST' });
      setDocs((prev) => prev.filter((d) => d.id !== id));
    } catch (err) {
      console.error(err);
    }
  };

  const onDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setDragOver(false);
      void handleFiles(e.dataTransfer.files);
    },
    [handleFiles],
  );

  return (
    <div className="relative min-h-screen overflow-hidden bg-background">
      {/* Aurora */}
      <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-[55vh]">
        <div
          className="absolute left-1/2 -top-20 -translate-x-1/2 rounded-[50%]"
          style={{
            width: '110vw',
            height: '55vh',
            background:
              'radial-gradient(ellipse at center top, rgba(139,92,246,0.20) 0%, rgba(74,108,247,0.08) 35%, transparent 70%)',
            filter: 'blur(80px)',
          }}
        />
      </div>

      {/* Slim sticky bar */}
      <div className="sticky top-0 z-10 border-b border-white/[0.04] bg-background/70 backdrop-blur-xl">
        <div className="mx-auto flex max-w-5xl items-center gap-3 px-4 py-3 sm:px-6">
          <button onClick={() => router.push('/')} className="size-8 inline-flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-colors">
            <ArrowLeft className="size-4" />
          </button>
          <GalexiaLogo size={22} />
          <span className="text-sm font-semibold">Documents</span>
        </div>
      </div>

      <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6 sm:py-14">
        {/* Hero */}
        <div className="mb-8 sm:mb-10 text-center">
          <p className="mb-3 text-[11px] font-medium uppercase tracking-[3px] text-violet-300/65">
            Your workspace
          </p>
          <h1
            className="font-normal text-foreground dark:text-[#F2F1F8]"
            style={{
              fontFamily: "'Playfair Display', Georgia, serif",
              fontSize: 'clamp(28px, 5vw, 42px)',
              letterSpacing: -0.5,
              lineHeight: 1.1,
            }}
          >
            Notes, slides, PDFs —{' '}
            <span
              className="italic"
              style={{
                background: 'linear-gradient(135deg, #6B8AFF, #A78BFA, #38BDF8)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              ready to use.
            </span>
          </h1>
          <p className="mt-3 text-sm sm:text-base text-muted-foreground/70 max-w-lg mx-auto">
            Drop in PDFs, docs, spreadsheets, screenshots, or photos of notes. Galexia parses them into clean Markdown for reading, search, and reuse.
          </p>
        </div>

        {/* Upload zone */}
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          className={`relative rounded-2xl border-2 border-dashed transition-all ${
            dragOver
              ? 'border-violet-400/60 bg-violet-500/[0.06]'
              : 'border-white/[0.08] bg-white/[0.015] hover:border-white/[0.16] hover:bg-white/[0.03]'
          } px-6 py-10 text-center`}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="application/pdf,text/plain,text/markdown,text/csv,application/json,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,image/png,image/jpeg,image/webp,image/gif"
            onChange={(e) => handleFiles(e.target.files)}
            className="hidden"
          />
          <div className="mx-auto inline-flex size-14 items-center justify-center rounded-2xl border border-violet-500/30 bg-violet-500/10 mb-4">
            <UploadSimple weight="duotone" className="size-6 text-violet-300" />
          </div>
          <p className="text-base font-medium text-foreground/95 mb-1.5">
            {dragOver ? 'Drop to upload' : uploading ? 'Uploading…' : 'Drop a file here, or click to choose'}
          </p>
          <p className="text-xs text-muted-foreground/60 mb-5">{ALLOWED_HINT}</p>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading || notMigrated}
            className="inline-flex items-center gap-1.5 rounded-xl bg-gradient-to-r from-[#3B5BD9] to-[#6B4FC7] px-5 py-2.5 text-sm font-semibold text-white shadow-[0_4px_24px_rgba(59,91,217,0.35)] transition-all hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
          >
            <UploadSimple weight="bold" className="size-4" />
            Choose files
          </button>

          {uploadError && (
            <div className="mt-5 inline-flex items-start gap-2 rounded-xl border border-destructive/30 bg-destructive/[0.06] px-4 py-2.5 text-left">
              <WarningCircle weight="duotone" className="size-4 mt-0.5 shrink-0 text-destructive" />
              <p className="text-xs text-destructive/95">{uploadError}</p>
            </div>
          )}
        </div>

        {/* Migration hint */}
        {notMigrated && (
          <div className="mt-6 rounded-xl border border-amber-500/25 bg-amber-500/[0.04] px-5 py-4 flex items-start gap-3">
            <Sparkle weight="duotone" className="size-5 shrink-0 mt-0.5 text-amber-400" />
            <div className="text-sm">
              <p className="font-medium text-amber-200/95">Workspace feature pending</p>
              <p className="mt-0.5 text-amber-100/65 text-xs">
                Run <code className="font-mono px-1 py-0.5 rounded bg-black/30">npm run db:generate &amp;&amp; npm run db:push</code> to enable document storage.
              </p>
            </div>
          </div>
        )}

        {/* Document list */}
        <div className="mt-10">
          <h2 className="text-sm font-medium uppercase tracking-[2px] text-muted-foreground/55 mb-4">
            Your documents{!loading && docs.length > 0 ? ` · ${docs.length}` : ''}
          </h2>

          {loading ? (
            <div className="space-y-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <div
                  key={i}
                  className="h-16 rounded-xl border border-white/[0.05] bg-white/[0.02]"
                  style={{ animation: `pulse 1.6s ease-in-out ${i * 0.08}s infinite` }}
                />
              ))}
            </div>
          ) : docs.length === 0 ? (
            <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] py-10 text-center text-sm text-muted-foreground/70">
              {notMigrated
                ? 'Upload will be available once the migration runs.'
                : 'No documents yet. Drop one in above.'}
            </div>
          ) : (
            <ul className="space-y-2">
              {docs.map((doc) => {
                const Icon = doc.mimeType === 'application/pdf' ? FilePdf : doc.mimeType.startsWith('image/') ? ImageSquare : FileIcon;
                const askPrompt = `Open my "${doc.name}" document from my workspace and summarize the key contents.`;
                const goToChat = () => {
                  router.push(`/?q=${encodeURIComponent(askPrompt)}`);
                };
                return (
                  <li
                    key={doc.id}
                    className="group flex items-center gap-3 rounded-xl border border-white/[0.06] bg-white/[0.02] px-3 py-3 sm:px-4 transition-colors hover:border-white/[0.14] hover:bg-white/[0.04]"
                  >
                    <button
                      type="button"
                      onClick={goToChat}
                      className="flex min-w-0 flex-1 items-center gap-3 text-left"
                      aria-label={`Open ${doc.name} in chat`}
                    >
                      <div className="size-10 shrink-0 inline-flex items-center justify-center rounded-lg border border-white/[0.06] bg-white/[0.03]">
                        <Icon weight="duotone" className="size-5 text-violet-300" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-foreground/95 group-hover:text-foreground transition-colors">{doc.name}</p>
                        <p className="text-[11px] text-muted-foreground/55">
                          {(doc.sizeBytes / 1024).toFixed(0)} KB · {new Date(doc.createdAt).toLocaleDateString()}
                          {doc.hasParsedMarkdown && (
                            <span className="ml-2 inline-flex items-center gap-1 text-emerald-400/80">
                              <CheckCircle weight="fill" className="size-3" />
                              Parsed
                            </span>
                          )}
                        </p>
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={goToChat}
                      className="inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-r from-[#3B5BD9] to-[#6B4FC7] px-3 py-1.5 text-xs font-semibold text-white shadow-[0_2px_12px_rgba(59,91,217,0.25)] transition-all hover:shadow-[0_2px_16px_rgba(59,91,217,0.4)] hover:scale-[1.03] active:scale-100"
                      title="Open this document in a new chat"
                    >
                      <ChatCircleDots weight="bold" className="size-3.5" />
                      <span className="hidden sm:inline">Use in chat</span>
                      <span className="sm:hidden">Use</span>
                    </button>
                    <ShareButton
                      title={doc.name}
                      mediaItem={{
                        kind: 'document',
                        url: doc.fileUrl,
                        title: doc.name,
                        mimeType: doc.mimeType,
                        sizeBytes: doc.sizeBytes,
                      }}
                    />
                    <a
                      href={doc.fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="size-8 inline-flex items-center justify-center rounded-md text-muted-foreground/55 hover:text-foreground hover:bg-white/[0.04] transition-colors"
                      aria-label="Open source file in new tab"
                      title="Open source file"
                    >
                      <ArrowSquareOut weight="duotone" className="size-4" />
                    </a>
                    <button
                      onClick={() => handleArchive(doc.id)}
                      className="size-8 inline-flex items-center justify-center rounded-md text-muted-foreground/55 hover:text-destructive hover:bg-destructive/[0.08] transition-colors"
                      aria-label="Archive"
                      title="Archive"
                    >
                      <Trash weight="duotone" className="size-4" />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
